import React, { useContext, useEffect, useRef, useState } from "react";
import { FeedbackContext } from "../../context/FeedbackContext";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Snackbar,
  useMediaQuery,
  InputLabel,
  OutlinedInput,
  Box,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { green } from "@mui/material/colors";
import { useConfig } from "../../hooks";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import FeedbackAPI from "@resys/feedback";

const feedbackUrl = "https://feedback.resys.io/api/feedback";

enum RequestState {
  NotSent,
  Loading,
  Success,
  Error,
}

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: 15,
  height: 15,
  background: theme.palette.common.white,
  color: theme.palette.common.black,
  "&:hover": {
    color: theme.palette.common.black,
    background: theme.palette.common.white,
  },
  "&> svg": {
    fontSize: "18px",
    transform: "scale(1.2,1.3)",
  },
}));

const StyledOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  height: "100%",
  padding: "0px",
  fontFamily: "FuturaCom-Book, Arial, Helvetica Neue, Helvetica, sans-serif",
  "& .MuiInputBase-input, .MuiOutlinedInput-input": {
    padding: "5px 5px",
    textAlign: "start",
  },
  "@media (max-width: 935px)": {
    width: "100%",
  },
}));

interface Props {}
export const Feedback: React.FC<Props> = () => {
  const intl = useIntl();
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const [description, setDescription] = useState("");
  const [requestState, setRequestState] = useState(RequestState.NotSent);
  //const [sendFeedback, setSendFeedback] = useState(false);
  const [name, setName] = useState("");
  const context = useContext(FeedbackContext);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const cfg = useConfig();
  const feedbackApiKey = cfg.config.feedbackApiKey;

  useEffect(() => {
    if (!descriptionRef.current || !context.isOpen) return;
    descriptionRef.current.focus();
  }, [context.isOpen]);

  useEffect(() => {
    if (context.isOpen) {
      // pre-import feedback library after user opens modal
      import("@resys/feedback");
    }
  }, [context.isOpen]);

  //if (!feedbackKey || !feedbackUrl) return null;
  if (!feedbackApiKey) return null;

  function close() {
    //setSendFeedback(false);
    setDescription("");
    setName("");
    setRequestState(RequestState.NotSent);
    context.close();
  }

  return (
    <>
      {requestState === RequestState.Success && (
        <Snackbar
          open={true}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          message={intl.formatMessage({ id: "feedback.thanks" })}
          sx={{ backgroundColor: green[600] }}
        />
      )}
      <Dialog
        open={context.isOpen && requestState !== RequestState.Success}
        onClose={close}
        fullScreen={fullScreen}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "500px",
            },
          },
        }}>
        <Box
          p="0 15px 0px 0px"
          display="flex"
          justifyContent="space-between"
          alignItems="center">
          <Typography sx={{ fontSize: "22px", pl: 1, marginTop: "15px" }}>
            <FormattedMessage id="feedback.title" />
          </Typography>
          <StyledIconButton
            onClick={() => {
              close();
            }}>
            <CloseIcon />
          </StyledIconButton>
        </Box>

        <DialogContent dividers sx={{ padding: "0 24px 24px 24px" }}>
          <InputLabel sx={{ mt: 2, mb: 1 }}>
            <FormattedMessage id={"feedback.description"} defaultMessage={""} />
          </InputLabel>
          <StyledOutlinedInput
            fullWidth={true}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={4}
            maxRows={5}
            inputRef={descriptionRef}
          />
          <InputLabel sx={{ mt: 2, mb: 1 }}>
            <FormattedMessage id={"feedback.name"} defaultMessage={""} />
          </InputLabel>
          <TextField
            fullWidth={true}
            value={name}
            onChange={(e) => setName(e.target.value)}
            variant="outlined"
          />
          {/*<CheckBox 
                checked={sendFeedback || false}
                onChange={() => setSendFeedback(!sendFeedback)}
                label={<FormattedMessage id={ 'feedback.sendScreenshot' }  />}
              />*/}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              setRequestState(RequestState.Loading);
              try {
                const feedback = new FeedbackAPI(
                  feedbackApiKey || "",
                  feedbackUrl
                );
                /*if(sendFeedback) {
                  const root = document.getElementById('root');
                  if(root) {
                    await feedback.screenshot(root);
                  }
                }*/
                feedback.addMetadata("Name", name);
                feedback.addMetadata("URL", window.location.href);
                feedback.addMetadata("Browser", navigator.userAgent);
                if (context.beforeSend) {
                  context.beforeSend(feedback);
                }
                await feedback.send(description);
                setRequestState(RequestState.Success);
                setTimeout(() => {
                  setRequestState(RequestState.NotSent);
                  close();
                }, 2500);
              } catch (e) {
                setRequestState(RequestState.Error);
              }
            }}>
            <FormattedMessage id="feedback.send" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
